"use client";

import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@os/components/ui/dialog";
import { Button } from "@os/components/ui/button";
import { Input } from "@os/components/ui/input";
import { Label } from "@os/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@os/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@os/components/ui/toggle-group";
import { Card } from "@os/components/ui/card";
import { AlignCenter, AlignLeft, AlignRight, Columns, Image, Palette, Quote, Type, LayoutTemplate } from "lucide-react";
import { cn } from "@os/lib/utils";

type LayoutKind =
  | "title"
  | "title-content"
  | "two-column"
  | "image-left"
  | "image-right"
  | "quote"
  | "section";

type BgMode = "default" | "light" | "dark" | "custom";
type TextMode = "theme" | "light" | "dark" | "custom";
type AlignMode = "left" | "center" | "right";

interface SlideTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (markdownSlide: string) => void;
}

const LayoutOption: React.FC<{
  value: LayoutKind;
  label: string;
  icon: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
}> = ({ value, label, icon, selected, onSelect }) => {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "group relative rounded-lg border p-3 text-left transition-all hover:shadow-sm w-full",
        selected ? "border-primary ring-2 ring-primary/30" : "border-border"
      )}
      title={label}
      aria-label={label}
    >
      <div className="flex items-center gap-2">
        <div className={cn("rounded-md p-2 text-muted-foreground bg-muted")}>{icon}</div>
        <div className="flex-1">
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-muted-foreground capitalize">{value.replace("-", " ")}</div>
        </div>
      </div>
      {/* Tiny preview bar */}
      <div className="mt-3 h-10 rounded-md border bg-gradient-to-br from-background to-muted/60 overflow-hidden">
        {/* Render a minimal visual hint per layout */}
        {value === "title" && <div className="h-full w-2/3 mx-auto my-2 rounded bg-foreground/10" />}
        {value === "title-content" && (
          <div className="h-full flex flex-col gap-1 p-2">
            <div className="h-2.5 w-1/2 rounded bg-foreground/15" />
            <div className="h-1.5 w-2/3 rounded bg-foreground/10" />
            <div className="h-1.5 w-1/2 rounded bg-foreground/10" />
          </div>
        )}
        {value === "two-column" && (
          <div className="h-full grid grid-cols-2 gap-1 p-2">
            <div className="rounded bg-foreground/10" />
            <div className="rounded bg-foreground/10" />
          </div>
        )}
        {value === "image-left" && (
          <div className="h-full grid grid-cols-3 gap-1 p-2">
            <div className="col-span-1 rounded bg-foreground/15" />
            <div className="col-span-2 flex flex-col gap-1">
              <div className="h-2.5 w-2/3 rounded bg-foreground/15" />
              <div className="h-1.5 w-3/4 rounded bg-foreground/10" />
              <div className="h-1.5 w-1/2 rounded bg-foreground/10" />
            </div>
          </div>
        )}
        {value === "image-right" && (
          <div className="h-full grid grid-cols-3 gap-1 p-2">
            <div className="col-span-2 flex flex-col gap-1">
              <div className="h-2.5 w-2/3 rounded bg-foreground/15" />
              <div className="h-1.5 w-3/4 rounded bg-foreground/10" />
              <div className="h-1.5 w-1/2 rounded bg-foreground/10" />
            </div>
            <div className="col-span-1 rounded bg-foreground/15" />
          </div>
        )}
        {value === "quote" && (
          <div className="h-full flex items-center justify-center p-2">
            <div className="h-2.5 w-3/4 rounded bg-foreground/15" />
          </div>
        )}
        {value === "section" && (
          <div className="h-full flex items-center justify-center p-2">
            <div className="h-2.5 w-1/2 rounded bg-foreground/15" />
          </div>
        )}
      </div>
    </button>
  );
};

const SlideTemplateModal: React.FC<SlideTemplateModalProps> = ({ open, onClose, onCreate }) => {
  const [layout, setLayout] = useState<LayoutKind>("title-content");
  const [bgMode, setBgMode] = useState<BgMode>("default");
  const [customBg, setCustomBg] = useState("#101010");
  const [textMode, setTextMode] = useState<TextMode>("theme");
  const [customText, setCustomText] = useState("#111111");
  const [align, setAlign] = useState<AlignMode>("left");
  const [bgImageUrl, setBgImageUrl] = useState("");

  const bgPreset = useMemo(() => {
    if (bgMode === "default") return undefined;
    if (bgMode === "light") return "#f7f7f9";
    if (bgMode === "dark") return "#0b0b0e";
    return customBg || "#101010";
  }, [bgMode, customBg]);

  const textPreset = useMemo(() => {
    if (textMode === "theme") return undefined;
    if (textMode === "light") return "#ffffff";
    if (textMode === "dark") return "#111111";
    return customText || "#111111";
  }, [textMode, customText]);

  const makeMarkdown = (): string => {
    const directives: string[] = [];
    if (bgPreset) directives.push(`::bg=${bgPreset}`);
    if (textPreset) directives.push(`::text=${textPreset}`);
    if (bgImageUrl.trim().length > 0) directives.push(`::bgImage=${bgImageUrl.trim()}`);
    
    // New: alignment as a first-class directive (no HTML wrapper)
    if (align) directives.push(`::align=${align}`);

    // Default content for each layout
    switch (layout) {
      case "title":
        return [
          ...directives,
          "",
          "# Slide Title",
          "Subtitle or tagline",
        ].join("\n");
      case "title-content":
        return [
          ...directives,
          "",
          "# Slide Title",
          "- Point one",
          "- Point two",
          "- Point three",
        ].join("\n");
      case "two-column":
        return [
          ...directives,
          "::layout=two-column",
          "",
          "## Left column",
          "",
          "- Bullet A",
          "- Bullet B",
          "||",
          "## Right column",
          "",
          "- Bullet C",
          "- Bullet D",
        ].join("\n");
      case "image-left": {
        const img = "https://picsum.photos/800/600";
        return [
          ...directives,
          "::layout=two-column",
          "",
          `![image](${img})`,
          "||",
          "## Headline",
          "",
          "- Supporting detail",
          "- Another detail",
        ].join("\n");
      }
      case "image-right": {
        const img = "https://picsum.photos/800/600";
        return [
          ...directives,
          "::layout=two-column",
          "",
          "## Headline",
          "",
          "- Supporting detail",
          "- Another detail",
          "||",
          `![image](${img})`,
        ].join("\n");
      }
      case "quote":
        return [
          ...directives,
          "",
          "> \"An inspiring quote goes here.\"",
          "",
          "— Someone",
        ].join("\n");
      case "section":
        return [
          ...directives,
          "",
          "# Section Title",
        ].join("\n");
      default:
        return [
          ...directives,
          "",
          "# New Slide",
          "- Bullet",
        ].join("\n");
    }
  };

  const handleCreate = () => {
    onCreate(makeMarkdown());
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="w-[680px] max-w-[95vw] p-0 overflow-hidden">
        <div className="bg-gradient-to-b from-background to-muted/40">
          <DialogHeader className="p-5 pb-3">
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="w-5 h-5 text-primary" />
              New slide
            </DialogTitle>
          </DialogHeader>

          <div className="px-5 pb-5 space-y-5">
            {/* Layout row */}
            <div>
              <div className="text-xs text-muted-foreground mb-2">Layout</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <LayoutOption
                  value="title"
                  label="Title"
                  icon={<Type className="w-4 h-4" />}
                  selected={layout === "title"}
                  onSelect={() => setLayout("title")}
                />
                <LayoutOption
                  value="title-content"
                  label="Title + Content"
                  icon={<Type className="w-4 h-4" />}
                  selected={layout === "title-content"}
                  onSelect={() => setLayout("title-content")}
                />
                <LayoutOption
                  value="two-column"
                  label="Two-column"
                  icon={<Columns className="w-4 h-4" />}
                  selected={layout === "two-column"}
                  onSelect={() => setLayout("two-column")}
                />
                <LayoutOption
                  value="image-left"
                  label="Image left"
                  icon={<Image className="w-4 h-4" />}
                  selected={layout === "image-left"}
                  onSelect={() => setLayout("image-left")}
                />
                <LayoutOption
                  value="image-right"
                  label="Image right"
                  icon={<Image className="w-4 h-4" />}
                  selected={layout === "image-right"}
                  onSelect={() => setLayout("image-right")}
                />
                <LayoutOption
                  value="quote"
                  label="Quote"
                  icon={<Quote className="w-4 h-4" />}
                  selected={layout === "quote"}
                  onSelect={() => setLayout("quote")}
                />
                <LayoutOption
                  value="section"
                  label="Section"
                  icon={<Type className="w-4 h-4" />}
                  selected={layout === "section"}
                  onSelect={() => setLayout("section")}
                />
              </div>
            </div>

            <Card className="p-4 space-y-4 border border-border/80 bg-card/95 backdrop-blur">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Palette className="w-4 h-4 text-muted-foreground" />
                    Card color
                  </div>
                  <Select value={bgMode} onValueChange={(v) => setBgMode(v as BgMode)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Background" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="custom">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {bgMode === "custom" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customBg}
                        onChange={(e) => setCustomBg(e.target.value)}
                        className="h-8 w-10 rounded-md border border-input bg-background"
                        aria-label="Custom background color"
                      />
                      <Input
                        className="h-9"
                        value={customBg}
                        onChange={(e) => setCustomBg(e.target.value)}
                        placeholder="#101010"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">Text color</div>
                  <Select value={textMode} onValueChange={(v) => setTextMode(v as TextMode)}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Text color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="theme">Theme</SelectItem>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="custom">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {textMode === "custom" && (
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        className="h-8 w-10 rounded-md border border-input bg-background"
                        aria-label="Custom text color"
                      />
                      <Input
                        className="h-9"
                        value={customText}
                        onChange={(e) => setCustomText(e.target.value)}
                        placeholder="#111111"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium">Text alignment</div>
                  <ToggleGroup
                    type="single"
                    value={align}
                    onValueChange={(v) => v && setAlign(v as AlignMode)}
                    className="justify-start"
                  >
                    <ToggleGroupItem value="left" aria-label="Align left" className="px-3">
                      <AlignLeft className="w-4 h-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem value="center" aria-label="Align center" className="px-3">
                      <AlignCenter className="w-4 h-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem value="right" aria-label="Align right" className="px-3">
                      <AlignRight className="w-4 h-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium">Backdrop image (optional)</div>
                  <Input
                    placeholder="https://…"
                    value={bgImageUrl}
                    onChange={(e) => setBgImageUrl(e.target.value)}
                  />
                </div>
              </div>
            </Card>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={handleCreate}>Add slide</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SlideTemplateModal;